import { cookieManager, SESSION_COOKIE_NAME } from '@/infrastructure/persistence/managers/CookieManager';
import { localStorageManager } from '@/infrastructure/persistence/managers/LocalStorageManager';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { authStore } from '@/infrastructure/store/authStore';

import { setGatewayAccessToken } from './gatewayFetch';
import { isPreviewSession, jwtExpiresInSeconds } from './previewSession';

const USER_EMAIL_KEY = 'user_email';

function decodeJWTPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function setRefreshUserEmail(email: string) {
  if (!email) return;
  localStorageManager.setItem(USER_EMAIL_KEY, email);
}

export function getRefreshUserEmail(): string | null {
  return localStorageManager.getItem<string>(USER_EMAIL_KEY);
}

function clearLocalSession(): void {
  authStore.getState().clearTokens();
  setGatewayAccessToken(null);
  localStorageManager.removeItem('access_token');
  localStorageManager.removeItem('refresh_token');
  localStorageManager.removeItem(USER_EMAIL_KEY);
  // Also drop the shared SSO cookie — otherwise sessionInitializer would silently
  // re-authenticate from it on the next visit, making logout reversible.
  cookieManager.delete(SESSION_COOKIE_NAME);
  cookieManager.deleteShared(SESSION_COOKIE_NAME);
}

export function logout() {
  const dashboardURL = process.env.NEXT_PUBLIC_DASHBOARD_URL;

  if (!dashboardURL) {
    logger.error('[AuthRefresh] NEXT_PUBLIC_DASHBOARD_URL not found');
    return;
  }

  clearLocalSession();
  window.location.href = dashboardURL.replace(/\/$/, '');
}

export function forceLogout() {
  const dashboardURL = process.env.NEXT_PUBLIC_DASHBOARD_URL;
  const appId = process.env.NEXT_PUBLIC_APP_ID;

  if (!dashboardURL || !appId) {
    logger.error('[AuthRefresh] NEXT_PUBLIC_DASHBOARD_URL or NEXT_PUBLIC_APP_ID not found');
    return;
  }

  clearLocalSession();

  const loginUrl = new URL(`${dashboardURL.replace(/\/$/, '')}/login`);
  loginUrl.searchParams.set('appId', appId);
  window.location.href = loginUrl.toString();
}

/**
 * Read-only preview "refresh": adopt the freshest access token from the shared
 * SSO cookie the parent tab keeps up to date. Returns null (caller fails quietly,
 * no redirect) when the cookie is missing or its own token has already expired.
 */
function adoptSharedSession(): string | null {
  const raw = cookieManager.get(SESSION_COOKIE_NAME);
  if (!raw) {
    return null;
  }
  try {
    const decoded = JSON.parse(atob(raw)) as { access_token?: string; refresh_token?: string };
    const accessToken = decoded.access_token;
    if (!accessToken) {
      return null;
    }
    const expiresIn = jwtExpiresInSeconds(accessToken);
    if (expiresIn !== null && expiresIn <= 5) {
      return null;
    }
    const refreshToken = decoded.refresh_token ?? localStorageManager.getItem<string>('refresh_token') ?? '';
    localStorageManager.setItem('access_token', accessToken);
    if (refreshToken) {
      localStorageManager.setItem('refresh_token', refreshToken);
    }
    authStore.getState().setTokens(accessToken, refreshToken);
    setGatewayAccessToken(accessToken);
    return accessToken;
  } catch (error) {
    logger.error('[AuthRefresh] Failed to adopt shared preview session', error);
    return null;
  }
}

// Single-flight refresh. Both the app's own gatewayFetch AND the embedded
// Brainwave widget can hit a 401 at the same moment and ask to refresh. The
// refresh token ROTATES, so two concurrent POST /auth/refresh-token would
// invalidate each other and the loser would forceLogout the user. Coalescing
// onto one in-flight refresh makes every caller share the same result.
let refreshInFlight: Promise<string | null> | null = null;

export function performTokenRefresh(): Promise<string | null> {
  if (refreshInFlight === null) {
    refreshInFlight = doTokenRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doTokenRefresh(): Promise<string | null> {
  // A preview session is a read-only mirror of the parent tab: it doesn't own a
  // refresh token, so hitting /auth/refresh-token (and forceLogout on failure)
  // would bounce the preview to a login screen. Mirror the parent's shared SSO
  // cookie instead — it stays fresh while the parent tab lives.
  if (isPreviewSession) {
    const adopted = adoptSharedSession();
    if (adopted === null) {
      // O token do cookie também venceu (a aba que abriu o preview não está mais
      // renovando). Sem isto o preview segue "logado" com um token morto e cada
      // tela repete 401 em silêncio. Derruba SÓ o estado local: o cookie é de
      // todos os apps da casa, e apagá-lo aqui deslogaria a pessoa de tudo.
      authStore.getState().clearTokens();
      setGatewayAccessToken(null);
    }
    return adopted;
  }

  const refreshToken = localStorageManager.getItem<string>('refresh_token');
  const currentAccessToken = localStorageManager.getItem<string>('access_token');

  if (!refreshToken || !currentAccessToken) {
    forceLogout();
    return null;
  }

  const storedEmail = localStorageManager.getItem<string>(USER_EMAIL_KEY);
  const payload = decodeJWTPayload(currentAccessToken);
  const payloadEmail = typeof payload?.email === 'string' ? (payload.email as string) : null;
  const email = storedEmail ?? payloadEmail;

  if (!email) {
    logger.error('[AuthRefresh] User email not available for refresh');
    forceLogout();
    return null;
  }

  const gatewayApiURL = process.env.NEXT_PUBLIC_BASE_API_GATEWAY_URL;

  if (!gatewayApiURL) {
    logger.error('[AuthRefresh] NEXT_PUBLIC_BASE_API_GATEWAY_URL is not defined');
    forceLogout();
    return null;
  }

  try {
    const res = await fetch(`${gatewayApiURL}/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, refreshToken }),
    });

    if (!res.ok) {
      forceLogout();
      return null;
    }

    const data = (await res.json()) as {
      access_token: string;
      refresh_token?: string;
    };

    const newAccessToken = data.access_token;
    const newRefreshToken = data.refresh_token ?? refreshToken;

    localStorageManager.setItem('access_token', newAccessToken);
    localStorageManager.setItem('refresh_token', newRefreshToken);
    authStore.getState().setTokens(newAccessToken, newRefreshToken);
    setGatewayAccessToken(newAccessToken);

    const sessionPayload = btoa(JSON.stringify({ access_token: newAccessToken, refresh_token: newRefreshToken }));
    cookieManager.setShared(SESSION_COOKIE_NAME, sessionPayload);

    return newAccessToken;
  } catch {
    forceLogout();
    return null;
  }
}
